import type { ReactElement } from 'react';

import { LegalDoc } from '@/widgets/legal-doc';
import { COMPANY, displayOrPending } from '@/shared/config';

const EFFECTIVE = '2026년 9월 1일';

/**
 * 환불정책 — 전자상거래법 제17조·제18조 기준.
 *
 * 오톱은 한 주문에 디지털과 실물이 섞이므로 규정이 갈린다.
 * 화면(P-21 상세, P-17 구매내역)과 같은 기준을 쓰지 않으면 분쟁이 난다.
 *
 * ⚠️ **공개 전에 반드시 정리해야 하는 것** — 우리 숙제라 화면에 띄우지 않고 여기 남긴다.
 *
 * 1. **수강률 기준**과 **배송비 부담 금액**이 임시값이다. 운영 기준을 확정한 뒤 바꿔야 한다.
 * 2. **통신판매업 신고번호**가 없다(`COMPANY.mailOrderNumber`). 미기재로 판매하면 위법이다.
 * 3. 법률 검토를 받지 않았다.
 */
export function RefundPage(): ReactElement {
  return (
    <LegalDoc
      title="환불정책"
      effectiveDate={EFFECTIVE}
      articles={[
        {
          id: 'scope',
          title: '적용 범위',
          body: (
            <p>
              이 정책은 {COMPANY.legalName}가 운영하는 {COMPANY.serviceName}에서 판매하는 온라인
              강의, 디지털 자료, 실물 상품 및 이들이 묶인 번들에 적용됩니다. 한 주문에 여러 유형이
              섞인 경우 <strong>상품 유형별로 각각의 기준</strong>을 적용합니다.
            </p>
          ),
        },
        {
          id: 'class',
          title: '온라인 강의',
          body: (
            <>
              <p>
                결제일로부터 7일 이내이고 <strong>수강률이 20% 미만</strong>인 경우 전액 환불합니다.
              </p>
              <p>
                수강률이 20% 이상이거나 결제 후 7일이 지난 경우, 전자상거래법 제17조 제2항 제5호에
                따라 청약철회가 제한될 수 있습니다. 다만 강의 내용이 표시·광고와 다르거나 계약
                내용과 다르게 이행된 경우에는 그 사실을 안 날부터 30일 이내, 공급받은 날부터 3개월
                이내에 청약철회를 할 수 있습니다.
              </p>
            </>
          ),
        },
        {
          id: 'digital',
          title: '디지털 자료 (템플릿 · 프롬프트 · 전자책)',
          body: (
            <>
              <p>
                <strong>다운로드 이력이 없는 경우</strong> 결제일로부터 7일 이내 전액 환불합니다.
              </p>
              <p>
                한 번이라도 다운로드하거나 복제 링크를 열람한 경우, 전송으로 이행이 완료된 것으로
                보아 청약철회가 제한됩니다. 이는 전자상거래법 제17조 제2항 제5호에 따른 것입니다.
              </p>
              <p>
                회사는 구매 화면과 다운로드 직전에 이 제한을 고지합니다. 고지하지 않은 경우에는
                제한을 주장하지 않습니다.
              </p>
            </>
          ),
        },
        {
          id: 'physical',
          title: '실물 상품',
          body: (
            <>
              <p>
                <strong>수령일로부터 7일 이내</strong> 단순변심 반품이 가능합니다. 이 경우 왕복
                배송비는 이용자가 부담합니다.
              </p>
              <p>
                상품이 파손·오배송되었거나 표시·광고와 다른 경우에는 수령일로부터 3개월 이내, 그
                사실을 안 날부터 30일 이내에 반품할 수 있으며 배송비는 회사가 부담합니다.
              </p>
              <p>
                다음의 경우 반품이 제한됩니다 — 이용자의 사용 또는 소비로 상품 가치가 현저히 감소한
                경우, 포장을 훼손하여 재판매가 곤란한 경우.
              </p>
            </>
          ),
        },
        {
          id: 'bundle',
          title: '번들 · 키트 (혼합 상품)',
          body: (
            <>
              <p>
                실물과 디지털이 함께 구성된 상품은 <strong>구성품별로 위 기준을 각각 적용</strong>
                하고, 환불 금액은 해당 구성품의 정가 비율로 산정합니다.
              </p>
              <p>
                예를 들어 디지털 구성품을 이미 내려받았다면 그 금액을 제외한 나머지가 환불 대상이
                됩니다. 각 구성품의 환불 대상 금액은 상품 상세 화면에 표시합니다.
              </p>
            </>
          ),
        },
        {
          id: 'free',
          title: '무료 자료',
          body: (
            <p>
              결제가 발생하지 않은 무료 자료는 환불 대상이 아닙니다. 자료 수령을 위해 생성된 계정은
              마이페이지에서 탈퇴할 수 있습니다.
            </p>
          ),
        },
        {
          id: 'process',
          title: '환불 절차',
          body: (
            <>
              <p>
                마이페이지 &gt; 구매내역에서 환불을 신청합니다. 실물 상품의 반품 회수는 배송
                조회에서 신청합니다.
              </p>
              <p>
                <strong>신청은 자동 승인되지 않습니다.</strong> 담당자가 청약철회 제한 사유 해당
                여부를 확인한 뒤 결과를 이메일로 알려드립니다.
              </p>
              <p>
                승인된 환불은 영업일 기준 3일 이내 처리하며, 카드 결제의 경우 카드사 사정에 따라
                취소 반영까지 추가 기간이 소요될 수 있습니다.
              </p>
            </>
          ),
        },
        {
          id: 'access',
          title: '환불 후 접근 권한',
          body: (
            <p>
              환불이 완료되면 해당 상품의 접근 권한은 즉시 회수됩니다. 다만 수강 진도 기록은
              보존되므로, 이후 다시 구매하면 이어서 볼 수 있습니다.
            </p>
          ),
        },
        {
          id: 'dispute',
          title: '분쟁 처리',
          body: (
            <>
              <p>환불 결과에 이의가 있는 경우 {COMPANY.email} 으로 재검토를 요청할 수 있습니다.</p>
              <p>
                협의가 이루어지지 않을 때에는 소비자분쟁조정위원회 또는 전자거래분쟁조정위원회에
                조정을 신청할 수 있습니다. 분쟁 처리는 소비자기본법 및 소비자분쟁해결기준에
                따릅니다.
              </p>
            </>
          ),
        },
        {
          id: 'contact',
          title: '문의처',
          body: (
            <ul className="flex flex-col gap-1">
              <li>상호 {COMPANY.legalName}</li>
              <li>사업자등록번호 {COMPANY.businessNumber}</li>
              <li>통신판매업신고 {displayOrPending(COMPANY.mailOrderNumber)}</li>
              <li>이메일 {COMPANY.email}</li>
              <li>전화 {displayOrPending(COMPANY.phone)}</li>
            </ul>
          ),
        },
      ]}
    />
  );
}
